import { isEmpty } from '@sgme/fp';
import { ProductList } from 'components/shared/ProductAndClientSelector/ProductList';
import { ProductPinnedEmptyList } from 'components/shared/ProductAndClientSelector/ProductPinnedEmptyList';
import type { IntlKey } from 'locales';
import { intersectionWith, sortBy } from 'lodash-es';
import type { ProductVariation } from 'models/productsReferential';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { getAvailableProductVariations, getUserPreferencesPinnedProductsVariations } from 'state/selectors';
import type { AppState } from 'state/state.model';
import type { WorkspaceWithProductSelector } from 'state/workspaces/workspaces/workspaces.model';

export interface ProductPinnedListProps {
  workspace: WorkspaceWithProductSelector;
  selectedProductVariation: Partial<ProductVariation>;
  onProductVariationSelected(product: Partial<ProductVariation>, quickStart: boolean): void;
}

export const ProductPinnedList: FC<ProductPinnedListProps> = ({
  workspace,
  selectedProductVariation,
  onProductVariationSelected,
}) => {
  const productVariations = useGetEntitledPinnedProductVariations(workspace);

  if (isEmpty(productVariations)) {
    return <ProductPinnedEmptyList />;
  }

  return (
    <ProductList
      className="h-100 border-bottom border-start border-end rounded-1 rounded-top"
      productVariations={productVariations}
      selectedProductVariation={selectedProductVariation}
      onProductVariationSelected={onProductVariationSelected}
      tabType="pinned"
      workspace={workspace}
    />
  );
};

function useGetEntitledPinnedProductVariations(workspace: WorkspaceWithProductSelector) {
  const { formatMessage } = useIntl();

  const entitledProductVariations = useSelector((state: AppState) => getAvailableProductVariations(state, workspace));

  const pinnedProductVariations = useSelector(getUserPreferencesPinnedProductsVariations(workspace));

  return sortBy(
    intersectionWith(
      entitledProductVariations,
      pinnedProductVariations,
      (a, b) => a.productType === b.productType && a.productSubType === b.productSubType && a.wrapper === b.wrapper,
    ),
    p => formatMessage({ id: `quote.products.${p.productSubType}` as IntlKey }),
  );
}
